// "use client"; // It was a client component. Could not figure out why
import React from "react";
import classNames from "classnames";

import { Block } from "../Layout";
import StyledList from "/components/StyledList";
import Image from "/components/cms/image";
import BikeAvailabilityStatusIcon from "/components/BikeAvailabilityStatusIcon";
import Button from "/components/Button";

import getMonthName from "/utils/getMonthName";

import { Flex } from "/components/Layout";

import styles from "./BikeModel.module.scss";
import buttonStyles from "../Button/Button.module.scss";

import { BikeItemContent } from "/app/(default)/sykler/[slug]/page";
import { getBikeModelAvailability } from "/lib/bike";

// This component was previously used in /sykler to render all bikes. Has been replaced by /web/components/cms/BikeGridList
// This component is used in rendering /sykler/{slug}. Remnants of when this component was both a grid display and a row display might be found.
// Might be used in /artikler. But, not sure where else.

// (thusanarul): I feel like this component could be refactored forever.
//               Did a refactoring to enable moving away from updateContentWithBikeAvailability()
//               See commit: 246f175d368

type ComponentData = {
  bikeModelData: BikeItemContent;
} & BikeItemContent["cms"];

const BikeModel = ({ data }: { data: ComponentData }) => {
  const { reverseFirst } = data || {};

  const gridClass = classNames({
    [styles.grid]: true,
    [styles[`grid-reverse`]]: reverseFirst
  });

  const gridItemClass = classNames({
    [styles.gridItem]: true,
    [styles[`gridItem-verticalCenter`]]: true
  });

  const formatQuantityText = ({
    quantity,
    deliveryDate
  }: {
    quantity: number;
    deliveryDate?: Date;
  }) => {
    if (quantity >= 10) {
      return "+10 på lager";
    }
    if (quantity < 10 && quantity > 0) {
      return `Kun ${quantity} igjen`;
    }
    if (deliveryDate) {
      return `Tilgjengelig fra ${getMonthName(deliveryDate)}`;
    }
  };

  const getAvailabilityText = ({
    availability
  }: {
    availability: ReturnType<typeof getBikeModelAvailability>;
  }) => {
    switch (availability) {
      case "available":
        return "Tilgjengelig nå";
      case "coming_soon":
        return "Kommer";
      default:
        return "Venteliste";
    }
  };

  const getCTADataFromAvailability = ({
    bikeModelData,
    availability
  }: {
    bikeModelData: BikeItemContent;
    availability: ReturnType<typeof getBikeModelAvailability>;
  }) => {
    switch (availability) {
      case "available":
        return {
          text: `Bestill sykkelen`,
          primary: true,
          href: `/sykler/bestill/${bikeModelData.bike.id}`
        };
      case "coming_soon":
        return {
          text: `Forhåndsbestill sykkelen`,
          primary: true,
          href: `/sykler/bestill/${bikeModelData.bike.id}`
        };
      default: {
        const bikeSlug = bikeModelData.cms.bikeModel.title.toLowerCase().replace(/\s/g, "-");
        return {
          text: `Meld meg på venteliste`,
          primary: true,
          href: `/sykler/venteliste?bikeType=${bikeSlug}`
        };
      }
    }
  };

  const ButtonGroup = ({
    priceContainerClass,
    bikeModelData
  }: {
    priceContainerClass: any;
    bikeModelData: BikeItemContent;
  }) => {
    const availability = getBikeModelAvailability(bikeModelData.bike);

    const noBikesAvailable = availability === "sold_out";

    const testRideOsloUrl = "https://calendly.com/wheetreff/provesykling";

    const bikeDetailsPage = bikeModelData.cms.bikeDetailsPage;

    const price = bikeModelData.bikeSubscriptions[0].price;

    const button = getCTADataFromAvailability({ bikeModelData, availability });
    const title = bikeModelData.cms.bikeModel.title;

    if (bikeDetailsPage) {
      return (
        <div className={styles.buttonGroup}>
          <Block top={5}>
            <div>
              <h2>
                Fra {price} <span className="body-text">kr/mnd</span>
              </h2>
            </div>
          </Block>

          {availability !== "sold_out" && (
            <Block top={4}>
              <Button link={button.href} primary>
                {button.text}
              </Button>
            </Block>
          )}

          <Block top={5}>
            <Button
              secondary={!noBikesAvailable}
              link={`/sykler/${
                noBikesAvailable ? "venteliste" : "interessentliste"
              }?bikeType=${title}`}
            >
              {noBikesAvailable ? "Sett meg på venteliste" : "Jeg vil vite mer!"}
            </Button>
          </Block>

          {/*Add CTA for test ride if no bikes available*/}
          {/* {noBikesAvailable && (
            <Block top={5}>
              <a
                className={classNames({
                  [buttonStyles.button]: true,
                  [buttonStyles[`button-secondary`]]: true
                })}
                href={testRideOsloUrl}
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                Bestill prøvesykling
              </a>
            </Block>
          )} */}
        </div>
      );
    }

    const linkItems = bikeModelData.cms.bikeModel.linkItems;

    if (linkItems.length > 0) {
      return (
        <div className={priceContainerClass}>
          <div>
            <h3>
              Fra {price} <span className="body-text">kr/mnd</span>
            </h3>
          </div>
          <Flex justify="start" align="center">
            {linkItems.map((link: { href: string; text: string }) => {
              return (
                <a key={link.href} className="highlight" href={link.href}>
                  {link?.text}
                </a>
              );
            })}
          </Flex>
        </div>
      );
    }
    return null;
  };

  const Grid = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => (
    <div className={gridClass}>
      {React.Children.map(children, (child) => (
        <div className={gridItemClass}>{child}</div>
      ))}
    </div>
  );

  const BikeModelCard = ({ bikeModelData }: { bikeModelData: BikeItemContent }) => {
    const title = bikeModelData.cms.bikeModel.title;

    const bike = bikeModelData;

    const quantity = bikeModelData.bike.bikesAvailableForSale;

    const image = bike.cms.bikeModel.image;
    const imageTitle = bike.cms.bikeModel.image.caption;
    const text = bike.cms.bikeModel.text;
    const styledList = bike.cms.bikeModel.styledList;
    const availability = getBikeModelAvailability(bike.bike);

    // An array of strings in the format year-month-date (i.e: 2024-03-24)
    const deliveryDate = bike.bike.bikeOrderEstimatedDeliveryDates[0]
      ? new Date(bike.bike.bikeOrderEstimatedDeliveryDates[0])
      : undefined;

    const soldOut = availability === "sold_out";
    const comingSoon = availability === "coming_soon";
    const available = availability === "available";

    const imageDeliveryTimeOverlayContainerClass = classNames({
      [styles.imageDeliveryTimeOverlayContainer]: true,
      [styles.soldOut]: soldOut,
      [styles.comingSoon]: comingSoon
    });
    const imageContainerClass = classNames({
      [styles.imageContainer]: true
    });
    const priceContainerClass = classNames({
      [styles.priceContainer]: true,
      [styles.reverse]: reverseFirst
    });

    return (
      <>
        <Grid>
          <div>
            {imageTitle && (
              <Block bottom={4}>
                <h3 className="h2">{imageTitle}</h3>
              </Block>
            )}

            <div className={imageContainerClass}>
              <div className={imageDeliveryTimeOverlayContainerClass}>
                <div className={styles.imageDeliveryTimeOverlay}>
                  <p className="text-label">{getAvailabilityText({ availability })}</p>
                </div>
              </div>

              {image && <Image imageObject={image} />}
            </div>
          </div>

          <Block top={4}>
            {title && <h3 className="h3">{title}</h3>}

            {(quantity > 0 || deliveryDate) && available && (
              <div className={styles.bikeAvailabilityContainer}>
                <>
                  <BikeAvailabilityStatusIcon soldOut={soldOut} />

                  <p className="text-label">{formatQuantityText({ quantity, deliveryDate })}</p>
                </>
              </div>
            )}

            {text && (
              <Block top={2}>
                <p className="body-text">{text}</p>
              </Block>
            )}

            {styledList && (
              <Block top={6}>
                <StyledList data={styledList} />
              </Block>
            )}

            <ButtonGroup priceContainerClass={priceContainerClass} bikeModelData={bikeModelData} />
          </Block>
        </Grid>
      </>
    );
  };

  const bikeModelData = data.bikeModelData;

  return (
    <Block top={6}>
      <BikeModelCard bikeModelData={bikeModelData} />
    </Block>
  );
};

export default BikeModel;
